import { useState, type FormEvent } from 'react';
import { useIdentity } from '../contexts/IdentityContext.js';
import { requestMagicLink, adminLogin, toIdentity } from '../auth/authApi.js';
import logoUrl from '../assets/losan-logo.png';

/**
 * Login for the demo deployment. Two paths:
 *
 *  - Agents (default): passwordless. Enter the email you're subscribed with and
 *    we send a one-time magic link. Response is always neutral (no account
 *    enumeration) — "if your email is registered, you'll get a link".
 *  - Admin: classic email + password, validated server-side.
 *
 * The site no longer sits behind a shared Basic Auth wall; this is the only
 * gate, and the real data endpoints require the session it produces.
 */
export function LoginScreen({ notice }: { notice?: string | null }) {
  const { setIdentity } = useIdentity();
  const [mode, setMode] = useState<'magic' | 'admin'>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onMagicSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const message = await requestMagicLink(email);
    setSent(message);
    setBusy(false);
  }

  async function onAdminSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const id = await adminLogin(email, password);
      setIdentity(toIdentity(id));
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <img className="login-logo" src={logoUrl} alt="LOSAN" />
        <h1>Portal del Agente</h1>

        {notice && <div className="login-error" role="alert">{notice}</div>}

        {mode === 'magic' ? (
          sent ? (
            <div className="login-success" role="status">
              <strong>Revisa tu correo</strong>
              <p>{sent}</p>
              <p className="login-hint">El enlace caduca en 15 minutos. Si no lo ves, revisa la carpeta de spam.</p>
              <button type="button" className="login-secondary" onClick={() => { setSent(null); setEmail(''); }}>
                Usar otro email
              </button>
            </div>
          ) : (
            <form onSubmit={onMagicSubmit}>
              <p className="login-lead">Introduce el correo con el que estás suscrito al portal y te enviaremos un enlace de acceso.</p>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                autoFocus
                required
              />
              <button type="submit" disabled={busy}>{busy ? 'Enviando…' : 'Sign in'}</button>
              <button type="button" className="login-link" onClick={() => { setMode('admin'); setError(null); }}>
                Acceso administrador
              </button>
            </form>
          )
        ) : (
          <form onSubmit={onAdminSubmit}>
            <label htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(null); }}
              autoFocus
              required
            />
            <label htmlFor="admin-password">Contraseña</label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(null); }}
              required
            />
            {error && <div className="login-error" role="alert">{error}</div>}
            <button type="submit" disabled={busy}>{busy ? 'Entrando…' : 'Entrar'}</button>
            <button type="button" className="login-link" onClick={() => { setMode('magic'); setError(null); setPassword(''); }}>
              ← Volver al acceso de agente
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
